import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErknmFormJudgment } from '../MainModule/interfaces/IKnmErknmFormJudgment';
import {
  getJudgmentSubjectDataItemBlockId,
  getJudgmentSubjectDataBlockId,
} from '../MainModule/constants/knmErknmFieldsElementsIds';
import { ILegalEntityLookup } from '../../APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { KnmCollapse } from '../../KnmPackage/MainModule/components/KnmCollapse';
import { ErknmSubjectInfo } from '../ErknmSubjectModule/components/ErknmSubjectInfo';
import { AddJudgmentModal } from './AddJudgmentModal';
import { IKnmErknmFormJudgmentSubjectData } from '../MainModule/interfaces/IKnmErknmFormJudgmentSubjectData';
import { IKnmErknmForm } from '../MainModule/interfaces/IKnmErknmForm';
import { ErknmJudgmentItemSubjectDataItem } from './ErknmJudgmentItemSubjectDataItem';
import { IKnmErknmFormSubject } from '../MainModule/interfaces/IKnmErknmFormSubject';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';

interface IErknmJudgmentItemSubjectDataProps {
  judgmentItemId: string;
  judgmentItemName: string;
  judgmentItemSubjectDataId: string;
  judgmentItemValue: IKnmErknmFormJudgment;
  watchOnly: boolean;
}

interface IErknmJudgmentItemSubjectDataStoreProps {
  formRef: IKnmFormRef | undefined;
}

const ErknmJudgmentItemSubjectDataWithoutStore = ({
  judgmentItemId,
  judgmentItemName,
  judgmentItemSubjectDataId,
  judgmentItemValue,
  watchOnly,
  formRef,
}: IErknmJudgmentItemSubjectDataProps & IErknmJudgmentItemSubjectDataStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const openModal = () => {
    const organizations = getOrganizations();

    if (organizations.length === 1) {
      // @ts-ignore
      const judgmentOrganizationList: IKnmErknmFormJudgmentSubjectData[] | undefined = formRef?.current.getFieldState(
        `${judgmentItemName}.judgmentSubjectData` as keyof IKnmErknmForm,
      )?.value;

      if (!judgmentOrganizationList?.map(x => x.organizationGuid).includes(organizations[0].guid) && push) {
        const newSubjectData: IKnmErknmFormJudgmentSubjectData = {
          organizationGuid: organizations[0].guid,
          isJudgmentAgainstOrganizations: undefined,
          isJudgmentAgainstOfficialPerson: undefined,
          judgmentAgainstOrganizations: undefined,
          judgmentAgainstOfficialPerson: undefined,
          cancellationActsAndDecisions: undefined,
          listId: uuidv4(),
          isNew: true,
        };

        push(`${judgmentItemName}.judgmentSubjectData`, newSubjectData);
      }
    } else {
      setIsModalVisible(true);
    }
  };

  const saveOrganizationModal = () => {
    setIsModalVisible(false);
  };

  const cancelModal = useCallback(() => {
    formRef?.current.change(
      `${judgmentItemName}.judgmentSubjectData` as keyof IKnmErknmForm,
      judgmentItemValue.judgmentSubjectData,
    );
    setIsModalVisible(false);
  }, [formRef, judgmentItemValue, judgmentItemName]);
  const makeRemoveJudgmentSubjectDataHandler = (index: number) => () => {
    !watchOnly && remove && remove(`${judgmentItemName}.judgmentSubjectData`, index);
  };

  const getOrganizations = () => {
    const organizations = formRef?.current.getFieldState('organizations')?.value || [];

    formRef?.current
      .getFieldState('acts')
      ?.value?.map(x => x.organization)
      .forEach(x => {
        if (x) {
          const actOrganization: IKnmErknmFormSubject = {
            ...x,
            inn: x.inn,
            ogrn: x.ogrn,
            position: undefined,
            okveds: x.okveds,
            addressFns: x.addressFns,
            organizationName: x.organizationName,
            firstOrganizationName: x.firstOrganizationName,
            mspCode: x.mspCode,
            mspRegistryDate: x.mspRegistryDate,
            checkSubjectResult: x.checkSubjectResult,
            dateCreate: x.dateCreate,
            isAutocomplete: x.isAutocomplete,
            isFiz: x.isFiz,
            guid: x.guid,
            isDocumentOrganization: x.isDocumentOrganization,
            listId: x.listId,
            isNew: false,
            nkoForeignAgentsRegistryNumber: x.nkoForeignAgentsRegistryNumber,
            nkoForeignAgentsRegistryInclusionDate: x.nkoForeignAgentsRegistryInclusionDate,
            nkoForeignAgentsRegistryExclusionDate: x.nkoForeignAgentsRegistryExclusionDate,
            isNko: x.isNko,
            subjectType: x.subjectType,
            kpp: x.kpp,
            smevRequestStatus: x.smevRequestStatus,
            rafpNza: x.rafpNza,
            isForeignSubjectAgencyNotRegistredInRf: x.isForeignSubjectAgencyNotRegistredInRf,
            country: x.country,
            isSonko: x.isSonko,
            locationAddress: x.locationAddress,
            workAddress: x.workAddress,
            factualAddress: x.factualAddress,
            snils: x.snils,
            document: x.document,
            epguNotifications: x.epguNotifications,
            ervkId: x.ervkId,
            isPresentInItRegistry: x.isPresentInItRegistry,
            itAccreditationResponse: x.itAccreditationResponse,
            smevItAccreditationRequestStatus: x.smevItAccreditationRequestStatus,
            inspectionResults: x.inspectionResults,
            locationregion: x.locationregion,
            locationnotInFIAS: x.locationnotInFIAS,
            locationmanualAddress: x.locationmanualAddress,
            locationfiasRegion: x.locationfiasRegion,
          };

          if (!organizations.map(org => org.guid).includes(actOrganization.guid)) {
            organizations.push(actOrganization);
          }
        }
      });

    return organizations;
  };

  useEffect(() => {
    if (getOrganizations().length === 1) {
      openModal();
    }
  }, [judgmentItemValue.judgmentSubjectData]);

  return (
    <>
      <AddJudgmentModal
        formRef={formRef}
        visible={isModalVisible}
        modalType={'organization'}
        judgmentItemName={judgmentItemName}
        onSave={saveOrganizationModal}
        onCancel={cancelModal}
        organizations={getOrganizations()}
      />
      <Section
        title="Сведения о контролируемом лице"
        type="list"
        id={judgmentItemSubjectDataId}
        titleId={getJudgmentSubjectDataBlockId(judgmentItemId)}
        customButton={
          getOrganizations().length !== 1 ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={watchOnly}
              onClick={openModal}
            />
          ) : undefined
        }
      >
        {!judgmentItemValue.judgmentSubjectData?.length && (
          <p className="shared-input-error">Поле не может быть пустым</p>
        )}
        <FieldArray name={`${judgmentItemName}.judgmentSubjectData`}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentSubjectData, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((judgmentItemSubjectDataItemName, index) => {
              const subjectDataValue: IKnmErknmFormJudgmentSubjectData = fields.value?.[index];
              const judgmentItemSubjectDataItemId = getJudgmentSubjectDataItemBlockId(
                judgmentItemId,
                subjectDataValue.listId,
              );
              const organizations = formRef?.current.getFieldState('organizations')?.value;
              const organization = organizations?.find(x => x.guid === subjectDataValue.organizationGuid);
              const organizationIndex = organizations?.findIndex(x => x.guid === subjectDataValue.organizationGuid);
              const hasError = checkHasErrorInCollapseBlock(formRef, judgmentItemSubjectDataItemName);

              if (organization) {
                return (
                  <KnmCollapse
                    theme="fields"
                    title="Контролирумое лицо"
                    id={judgmentItemSubjectDataItemId}
                    isNew={judgmentItemValue.isNew}
                    isNewTitle={'Новое'}
                    opened={true}
                    onClose={!watchOnly ? makeRemoveJudgmentSubjectDataHandler(index) : undefined}
                    key={judgmentItemSubjectDataItemName}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [judgmentItemSubjectDataItemName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[judgmentItemSubjectDataItemName]}
                  >
                    <>
                      <ErknmSubjectInfo
                        name={`organizations[${organizationIndex}]`}
                        isWatchOnly={watchOnly || !!organization.ervkId}
                        isNewOrganizationTemplate={false}
                        isExtended={false}
                        isAutocomplete={organization.isAutocomplete}
                        inn={organization.inn}
                        innLabelBlockId={`organizations[${organizationIndex}].innLabelBlock`}
                        ogrn={organization.ogrn}
                        ogrnLabelBlockId={`organizations[${organizationIndex}].ogrnLabelBlock`}
                        firstOrganizationName={organization.firstOrganizationName}
                        organizationNameBlockId={`organizations[${organizationIndex}].organizationNameBlock`}
                        subjectResult={organization.checkSubjectResult}
                        lookupData={lookupData}
                        setLookupData={setLookupData}
                        subjectType={organization.subjectType}
                        kpp={organization.kpp}
                        smevRequestStatus={organization.smevRequestStatus}
                        rafpNza={organization.rafpNza}
                        snils={organization.snils}
                        rafpLabelBlockId={`organizations[${organizationIndex}].rafpNzaLabelBlock`}
                        kppLabelBlockId={`organizations[${organizationIndex}].kppLabelBlock`}
                        checkSubjectResult={organization.checkSubjectResult}
                        ervkId={organization.ervkId}
                      />
                      {subjectDataValue.organizationGuid && (
                        <ErknmJudgmentItemSubjectDataItem
                          judgmentItemName={judgmentItemName}
                          judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
                          judgmentItemValue={judgmentItemValue}
                          watchOnly={watchOnly}
                          cancellationActsAndDecisions={subjectDataValue.cancellationActsAndDecisions}
                          isJudgmentAgainstOfficialPerson={subjectDataValue.isJudgmentAgainstOfficialPerson}
                          isJudgmentAgainstOrganizations={subjectDataValue.isJudgmentAgainstOrganizations}
                          judgmentAgainstOrganizations={subjectDataValue.judgmentAgainstOrganizations}
                          judgmentAgainstOfficialPerson={subjectDataValue.judgmentAgainstOfficialPerson}
                          judgmentItemSubjectDataItemId={judgmentItemSubjectDataItemId}
                          organizationGuid={subjectDataValue.organizationGuid}
                        />
                      )}
                    </>
                  </KnmCollapse>
                );
              }

              return <></>;
            });
          }}
        </FieldArray>
      </Section>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmJudgmentItemSubjectDataStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmJudgmentItemSubjectData = connect(mapStateToProps)(ErknmJudgmentItemSubjectDataWithoutStore);
